import { useMemo, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import ButtonControls from './components/ButtonControls';
import GeneratedCode from './components/GeneratedCode';
import Dashboard from './components/Dashboard';
import {
  createButtonCode,
  getHoverPreviewStyle
} from './utils/buttonCode';
import './App.css';
import './Dashboard.css';

const { FiCode, FiEye, FiGrid } = FiIcons;

function ButtonGenerator({ onBack }) {
  const [settings, setSettings] = useState({
    label: 'Get started',
    url: 'https://example.com',
    textColor: '#002831',
    backgroundColor: '#38D39F',
    gradient: false,
    gradientStart: '#007D7B',
    gradientEnd: '#38D39F',
    gradientDirection: '135deg',
    borderColor: '#38D39F',
    borderWidth: 0,
    borderStyle: 'solid',
    borderRadius: 12,
    paddingVertical: 13,
    paddingHorizontal: 22,
    bold: true,
    italic: false,
    hoverEffect: 'lift',
    icon: 'fa-arrow-right',
    iconPosition: 'right'
  });
  const [mode, setMode] = useState('inline');
  const [openInNewTab, setOpenInNewTab] = useState(false);
  const [isPreviewHovered, setIsPreviewHovered] = useState(false);
  const code = useMemo(
    () => createButtonCode(settings, mode, openInNewTab),
    [settings, mode, openInNewTab]
  );

  const previewStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    fontWeight: settings.bold ? 700 : 'inherit',
    fontStyle: settings.italic ? 'italic' : 'inherit',
    color: settings.textColor,
    background: settings.gradient
      ? `linear-gradient(${settings.gradientDirection},${settings.gradientStart},${settings.gradientEnd})`
      : settings.backgroundColor,
    border: settings.borderWidth
      ? `${settings.borderWidth}px ${settings.borderStyle} ${settings.borderColor}`
      : 'none',
    borderRadius: `${settings.borderRadius}px`,
    padding: `${settings.paddingVertical}px ${settings.paddingHorizontal}px`,
    textDecoration: 'none',
    textAlign: 'center',
    lineHeight: 1.2,
    transition: 'transform 180ms ease,box-shadow 180ms ease,filter 180ms ease',
    ...getHoverPreviewStyle(settings.hoverEffect, isPreviewHovered)
  };

  return (
    <main className="app-shell">
      <header className="topbar">
        <a
          className="brand"
          href="#"
          aria-label="Shift Creative Tools dashboard"
          onClick={(event) => {
            event.preventDefault();
            onBack();
          }}
        >
          <span className="brand-mark"><SafeIcon icon={FiGrid} /></span>
          <span>shift<span className="brand-accent">creative tools</span></span>
        </a>
        <button className="dashboard-back" type="button" onClick={onBack}>
          <span>←</span> All tools
        </button>
      </header>

      <section className="intro">
        <div className="eyebrow">DESIGN · PREVIEW · COPY</div>
        <h1>Your button,<span>your style.</span></h1>
        <p>Build a button and copy a self-contained code snippet.</p>
      </section>

      <div className="workspace">
        <section className="panel controls-panel">
          <div className="panel-heading">
            <div className="heading-icon"><SafeIcon icon={FiGrid} /></div>
            <div><h2>Customize</h2><p>Make it feel like yours</p></div>
          </div>
          <ButtonControls
            settings={settings}
            onChange={setSettings}
            openInNewTab={openInNewTab}
            onOpenInNewTabChange={setOpenInNewTab}
          />
        </section>

        <div className="right-column">
          <section className="panel preview-panel">
            <div className="section-heading">
              <div><SafeIcon icon={FiEye} /><h2>Live preview</h2></div>
              <span className="live-indicator"><i />LIVE</span>
            </div>
            <div className="preview-stage">
              <p className="sample-paragraph">
                Good design makes the next step feel easy.{' '}
                <span
                  aria-label={`Button preview: ${settings.label || 'Click Here'}`}
                  style={previewStyle}
                  onMouseEnter={() => setIsPreviewHovered(true)}
                  onMouseLeave={() => setIsPreviewHovered(false)}
                >
                  {settings.iconPosition === 'left' && settings.icon !== 'none' && (
                    <i className={`fas ${settings.icon}`} aria-hidden="true" />
                  )}
                  <span>{settings.label || 'Click Here'}</span>
                  {settings.iconPosition !== 'left' && settings.icon !== 'none' && (
                    <i className={`fas ${settings.icon}`} aria-hidden="true" />
                  )}
                </span>
              </p>
            </div>
            <p className="inherit-note">
              <span>i</span> Hover to preview the selected interaction.
            </p>
          </section>

          <section className="panel code-panel">
            <div className="section-heading code-heading">
              <div><SafeIcon icon={FiCode} /><h2>Copy your code</h2></div>
              <span className="html-badge">HTML</span>
            </div>
            <GeneratedCode
              code={code}
              mode={mode}
              onModeChange={setMode}
            />
          </section>
        </div>
      </div>

      <footer>Made for the little details that make a big difference.</footer>
    </main>
  );
}

function App() {
  const [activeTool, setActiveTool] = useState(null);

  if (activeTool === 'button-generator') {
    return <ButtonGenerator onBack={() => setActiveTool(null)} />;
  }

  return (
    <Dashboard onOpenButtonGenerator={() => setActiveTool('button-generator')} />
  );
}

export default App;